import { tr } from './i18n/i18n.ts';
/**
 * Klient mot AutoDay-API:t (egen server). Webb: sessionscookie (httpOnly, Secure, SameSite=Lax) –
 * ingen token i JS. Native: bärartoken från plattformens säkra lagring (se auth/secureToken.ts).
 *
 * Varje anrop till AutoDays egna API skickar `x-autoday-user` = kontot som den aktiva lokala profilen
 * tillhör. Servern avvisar med ACCOUNT_MISMATCH om sessionen tillhör ett annat konto (t.ex. om en
 * annan flik loggat in som någon annan), så att A:s lokala data aldrig synkas till B.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly body?: unknown;
  /** Sekunder att vänta vid 429, om servern angav det. */
  readonly retryAfter: number | null;
  constructor(status: number, code: string, message: string, body?: unknown, retryAfter: number | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
    this.retryAfter = retryAfter;
  }
}

export interface ApiTarget {
  /** '' = samma ursprung som sidan. */
  base: string;
  token?: string | null;
  expectedUser?: string | null;
}

export interface ApiInit {
  method?: string;
  body?: unknown;
  timeoutMs?: number;
  idempotencyKey?: string;
}

let defaults: { base: string; token: (() => Promise<string | null>) | null; expectedUser: () => string | null } = {
  base: '',
  token: null,
  expectedUser: () => null,
};

export function configureApi(opts: { baseUrl?: string; token?: () => Promise<string | null>; expectedUser?: () => string | null }) {
  if (opts.baseUrl !== undefined) defaults = { ...defaults, base: opts.baseUrl.replace(/\/$/, '') };
  if (opts.token) defaults = { ...defaults, token: opts.token };
  if (opts.expectedUser) defaults = { ...defaults, expectedUser: opts.expectedUser };
}

export function currentApiBase(): string {
  return defaults.base;
}

/** Anrop mot den aktiva profilens server. */
export async function api<T>(path: string, init: ApiInit = {}): Promise<T> {
  const token = defaults.token ? await defaults.token() : null;
  return (await apiAt<T>({ base: defaults.base, token, expectedUser: defaults.expectedUser() }, path, init)).data;
}

/** Som api() men returnerar även svarshuvuden. */
export async function apiWithHeaders<T>(path: string, init: ApiInit = {}): Promise<{ data: T; headers: Headers }> {
  const token = defaults.token ? await defaults.token() : null;
  return apiAt<T>({ base: defaults.base, token, expectedUser: defaults.expectedUser() }, path, init);
}

/** Anrop mot en uttrycklig server (används av inloggningsflödet innan profilen bytts). */
export async function apiAt<T>(target: ApiTarget, path: string, init: ApiInit = {}): Promise<{ data: T; headers: Headers }> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), init.timeoutMs ?? 15000);
  const headers: Record<string, string> = { accept: 'application/json' };
  if (init.body !== undefined) headers['content-type'] = 'application/json';
  if (init.idempotencyKey) headers['idempotency-key'] = init.idempotencyKey;
  if (target.token) headers['authorization'] = `Bearer ${target.token}`;
  if (target.expectedUser && !path.startsWith('/api/auth/')) headers['x-autoday-user'] = target.expectedUser;
  try {
    const res = await fetch(target.base.replace(/\/$/, '') + path, {
      method: init.method ?? (init.body !== undefined ? 'POST' : 'GET'),
      headers,
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
      credentials: 'include',
      signal: ctrl.signal,
      // Aldrig följa omdirigeringar till annan server med inloggningsuppgifter.
      redirect: 'error',
    });
    const text = await res.text();
    let data: unknown = null;
    try {
      data = text ? (JSON.parse(text) as unknown) : null;
    } catch {
      if (!res.ok) throw new ApiError(res.status, res.status === 404 ? 'NOT_AUTODAY' : 'HTTP_' + res.status, res.status === 404 ? tr('Ingen AutoDay-server svarade på adressen.', 'No AutoDay server responded at the address.') : tr(`Servern svarade ${res.status}.`, `The server responded ${res.status}.`));
      throw new ApiError(res.status, 'BAD_RESPONSE', tr('Servern gav ett oväntat svar.', 'The server gave an unexpected response.'));
    }
    if (!res.ok) {
      const d = (data ?? {}) as { code?: string; message?: string };
      const ra = Number(res.headers.get('x-retry-after') ?? res.headers.get('retry-after'));
      throw new ApiError(res.status, d.code ?? 'HTTP_' + res.status, d.message ?? `Servern svarade ${res.status}.`, data, Number.isFinite(ra) && ra > 0 ? Math.ceil(ra) : null);
    }
    return { data: data as T, headers: res.headers };
  } catch (e) {
    if (e instanceof ApiError) throw e;
    if ((e as Error).name === 'AbortError') throw new ApiError(0, 'TIMEOUT', 'Servern svarade inte i tid.');
    throw new ApiError(0, 'NETWORK', 'Ingen kontakt med AutoDay-servern.');
  } finally {
    clearTimeout(t);
  }
}
